import React, { useEffect, useMemo, useRef, useState } from "react";
import "./css/listing-detail.css";
import AvailabilityCard from "./AvailabilityCard";

// Detail page that consumes a listing object (from SwiperGallery fetch)
// Props:
// - listing: { title, city, image, pictures[], guests, bedrooms, bathrooms, price }
const ListingDetail = ({ listing }) => {
  const isProd = process.env.NODE_ENV === "production";
  const [bookingHtml, setBookingHtml] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [prefillSearch, setPrefillSearch] = useState({
    start: null,
    end: null,
    guests: 1,
  });
  const [autoPrefilled, setAutoPrefilled] = useState(false);
  const [autoPrefillActive, setAutoPrefillActive] = useState(false);

  const toFiniteNumber = (value) => {
    if (value == null) return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const hasHebrew = (text) => /[\u0590-\u05FF]/.test(String(text));

  const normalizeKey = (value) =>
    String(value)
      .trim()
      .toLowerCase()
      .replace(/[’']/g, "'")
      .replace(/\s+/g, " ");

  const parseDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const cityLabel = (city) => {
    if (city == null) return "";
    const text = String(city).trim();
    if (!text) return text;
    if (hasHebrew(text)) return text;

    const map = {
      eilat: "אילת",
      israel: "ישראל",
      "tel aviv": "תל אביב",
      jerusalem: "ירושלים",
      haifa: "חיפה",
    };

    return text
      .split(",")
      .map((part) => {
        const trimmed = part.trim();
        const normalized = trimmed.toLowerCase();
        return map[normalized] || trimmed;
      })
      .join(", ");
  };

  const formatTimeHebrew = (value) => {
    if (value == null) return "";
    const text = String(value).trim();
    if (!text) return text;

    const match = text.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return text;

    let hours = Number(match[1]);
    const minutes = match[2];
    const ampm = match[3].toUpperCase();

    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;

    return `${String(hours).padStart(2, "0")}:${minutes}`;
  };

  const propertyTypeLabel = (value) => {
    if (value == null) return "";
    const text = String(value).trim();
    if (!text) return text;
    if (hasHebrew(text)) return text;
    const map = {
      apartment: "דירה",
      studio: "סטודיו",
      villa: "וילה",
      house: "בית",
      "entire apartment": "דירה",
    };
    const translated = map[normalizeKey(text)];
    if (translated) return translated;
    return isProd ? "" : text;
  };

  const roomTypeLabel = (value) => {
    if (value == null) return "";
    const text = String(value).trim();
    if (!text) return text;
    if (hasHebrew(text)) return text;
    const map = {
      "entire home/apt": "כל הדירה/הבית",
      "private room": "חדר פרטי",
      "shared room": "חדר משותף",
    };
    const translated = map[normalizeKey(text)];
    if (translated) return translated;
    return isProd ? "" : text;
  };

  const neighborhoodLabel = (value) => {
    if (value == null) return "";
    const text = String(value).trim();
    if (!text) return text;
    if (hasHebrew(text)) return text;
    const map = {
      seaside: "סי סייד",
    };
    const translated = map[normalizeKey(text)];
    if (translated) return translated;
    return isProd ? "" : text;
  };

  const titleLabel = (value) => {
    if (value == null) return "";
    const text = String(value).trim();
    if (!text) return text;
    return text.replace(/\s*-\s*by\s+edom\s*$/i, " - מאת Edom");
  };

  const amenityLabel = (amenity) => {
    if (amenity == null) return "";
    const text = String(amenity).trim();
    if (!text) return text;

    if (hasHebrew(text)) return text;

    const normalized = normalizeKey(text);
    const map = {
      // Codes / common amenities
      pool: "בריכה",
      "swimming pool": "בריכה",
      "communal pool": "בריכה משותפת",
      "outdoor pool": "בריכה חיצונית",
      "sea view": "נוף ים",
      "beach view": "נוף לים",
      "water view": "נוף למים",
      "mountain view": "נוף להרים",
      sea: "נוף ים",
      beach: "חוף",
      "beach front": "על קו החוף",
      parking: "חניה",
      "free parking": "חניה חינם",
      "free parking on premises": "חניה חינם במקום",
      "parking on premises": "חניה במקום",
      wifi: "WIFI",
      "wi-fi": "WIFI",
      "wireless internet": "WIFI",
      internet: "אינטרנט",
      balcony: "מרפסת",
      "patio or balcony": "מרפסת",
      patio: "פטיו",
      kitchen: "מטבח",
      kitchenette: "מטבחון",
      ac: "מיזוג",
      "air conditioning": "מיזוג",
      heating: "חימום",
      tv: "טלוויזיה",
      television: "טלוויזיה",
      "cable tv": "טלוויזיה בכבלים",
      elevator: "מעלית",
      lift: "מעלית",
      washer: "מכונת כביסה",
      "washing machine": "מכונת כביסה",
      dryer: "מייבש כביסה",
      "hair dryer": "מייבש שיער",
      iron: "מגהץ",
      "bed linens": "מצעים",
      "extra pillows and blankets": "כריות ושמיכות נוספות",
      hangers: "קולבים",
      "private entrance": "כניסה פרטית",
      "smoke alarm": "גלאי עשן",
      "smoke detector": "גלאי עשן",
      "carbon monoxide alarm": "גלאי פחמן חד-חמצני",
      "carbon monoxide detector": "גלאי פחמן חד-חמצני",
      "first aid kit": "ערכת עזרה ראשונה",
      "fire extinguisher": "מטף כיבוי",
      "emergency exit": "יציאת חירום",
      doorman: "שומר/שוער",
      crib: "עריסה",
      desk: "שולחן עבודה",
      coffee: "קפה",
      "coffee maker": "מכונת קפה",
      kettle: "קומקום",
      microwave: "מיקרוגל",
      refrigerator: "מקרר",
      stove: "כיריים",
      toaster: "טוסטר",
      cookware: "כלי בישול",
      "dishes and silverware": "כלי אוכל וסכו״ם",
      "wine glasses": "כוסות יין",
      "children's dinnerware": "כלי אוכל לילדים",
      "children’s dinnerware": "כלי אוכל לילדים",
      "laptop friendly workspace": "פינת עבודה מותאמת ללפטופ",
      "luggage dropoff allowed": "אפשר להשאיר מזוודות",
      "room-darkening shades": "וילונות האפלה",
      shopping: "קניות",
      "water sports": "ספורט ימי",
      "family/kid friendly": "מתאים למשפחות",
      "suitable for children (2-12 years)": "מתאים לילדים (2–12)",
      "suitable for infants (under 2 years)": "מתאים לתינוקות (מתחת לגיל 2)",

      // Current fallback amenities (English → Hebrew)
      "single level home": "דירה בקומה אחת",
      "body soap": "סבון גוף",
      "cleaning products": "חומרי ניקוי",
      conditioner: "מרכך",
      "hot water": "מים חמים",
      shampoo: "שמפו",
      "shower gel": "ג׳ל רחצה",
      "towels provided": "מגבות מסופקות",
    };

    const translated = map[normalized];
    if (translated) return translated;

    // בפרודקשן: חובה עברית (אם אין תרגום והטקסט באנגלית, לא מציגים).
    // בדיבלופמנט: מציגים את הערך המקורי כדי שיהיה קל לזהות מה חסר לתרגום.
    return isProd ? "" : text;
  };

  const fallback = useMemo(
    () => ({
      title: "דירת גן מפנקת במתחם סי סייד 100 מטר מהים",
      city: "אילת, ישראל",
      reviews: { avg: 4.8, total: 62 },
      pictures: [
        "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1500&q=80",
        "https://images.unsplash.com/photo-1505692069463-5e3405e1e319?auto=format&fit=crop&w=900&q=80",
        "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=900&q=80",
        "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=900&q=80",
      ],
      price: "₪690.00",
      guests: 4,
      bedrooms: 1,
      beds: 2,
      bathrooms: 1,
      amenities: [
        "דירה בקומה אחת",
        "סבון גוף",
        "חומרי ניקוי",
        "מרכך",
        "מים חמים",
        "שמפו",
        "ג׳ל רחצה",
        "מגבות מסופקות",
      ],
      summary:
        "ברוכים הבאים לחופשה מפנקת ומושלמת באילת. דירה קסומה ומעוצבת בקפידה במתחם סי סייד, ממוקמת קרוב לים עם נוף פנורמי עוצר נשימה.",
      description:
        "דירה זו מציעה חוויית אירוח מושלמת עם מיטה זוגית מפנקת וספה נפתחת. מטבח מאובזר, פינת אוכל, מרפסת דק עם נוף לים. כל הפרטים הקטנים כדי להבטיח שהחופשה שלכם תהיה נוחה ורגועה.",
      neighborhood:
        "הדירה נמצאת במיקום מעולה: 100 מטר מהים וקרוב לחופים, מסעדות ומרכזי בילוי.",
      access: "כל הדירה לרשותכם, כולל מרפסת וגישה לחניה אחת.",
      checkin: "15:00",
      checkout: "11:00",
      coords: { lat: 29.55, lng: 34.95 },
    }),
    [],
  );

  const rawPictures =
    listing?.pictures && listing.pictures.length
      ? listing.pictures
      : fallback.pictures;
  const normalizedPictures = rawPictures
    .map((p) => (typeof p === "string" ? p : p.original))
    .filter(Boolean);

  const data = {
    id:
      listing?.id ||
      new URLSearchParams(window.location.search).get("listingId") ||
      "demo-listing",
    ...fallback,
    ...listing,
    pictures: normalizedPictures.length
      ? normalizedPictures
      : fallback.pictures,
    reviews: listing?.reviews || fallback.reviews,
    amenities: listing?.amenities?.length
      ? listing.amenities
      : fallback.amenities,
  };

  const bedroomsCount = toFiniteNumber(data.bedrooms);
  const bathroomsCount = toFiniteNumber(data.bathrooms);
  const guestsCount = toFiniteNumber(data.guests);
  const bedroomsLabel =
    bedroomsCount && bedroomsCount > 0
      ? `${bedroomsCount} ${bedroomsCount === 1 ? "חדר שינה" : "חדרי שינה"}`
      : "לא צוין";
  const bathroomsLabel =
    bathroomsCount && bathroomsCount > 0
      ? `${bathroomsCount} ${bathroomsCount === 1 ? "חדר רחצה" : "חדרי רחצה"}`
      : "לא צוין";
  const guestsLabel =
    guestsCount && guestsCount > 0
      ? `${guestsCount} ${guestsCount === 1 ? "אורח" : "אורחים"}`
      : "לא צוין";

  const bedsFromData =
    toFiniteNumber(data.beds) ??
    toFiniteNumber(data.bedsCount) ??
    toFiniteNumber(data.bedCount) ??
    null;
  const bedsFromBedrooms = toFiniteNumber(data.bedrooms);
  const bedsCount =
    bedsFromData && bedsFromData > 0
      ? bedsFromData
      : bedsFromBedrooms && bedsFromBedrooms > 0
        ? bedsFromBedrooms
        : null;
  const bedsLabel =
    bedsCount && bedsCount > 0
      ? `${bedsCount} ${bedsCount === 1 ? "מיטה" : "מיטות"}`
      : "לא צוין";

  const slides = useMemo(() => {
    const arr =
      data.pictures && data.pictures.length
        ? data.pictures
        : data.image
          ? [data.image]
          : [];
    return arr;
  }, [data.image, data.pictures]);

  // const mainImage = slides[0] || data.image;
  const thumbs = slides.slice(1, 4);
  const lat = data.coords?.lat ?? 29.55;
  const lng = data.coords?.lng ?? 34.95;
  const delta = 0.02;
  const sliderId = useMemo(
    () => `ld-swiper-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );
  const paginationId = `${sliderId}-pagination`;
  const swiperRef = useRef(null);
  const [swiperReady, setSwiperReady] = useState(false);

  useEffect(() => {
    if (window.Swiper) {
      setSwiperReady(true);
      return;
    }
    const poll = setInterval(() => {
      if (window.Swiper) {
        clearInterval(poll);
        setSwiperReady(true);
      }
    }, 100);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (!swiperReady || !window.Swiper || !slides.length) return;
    const container = document.querySelector(`#${sliderId}`);
    if (!container) return;
    if (swiperRef.current) {
      swiperRef.current.destroy(true, true);
    }
    // eslint-disable-next-line no-undef
    swiperRef.current = new Swiper(`#${sliderId}`, {
      slidesPerView: 1,
      loop: slides.length > 1,
      spaceBetween: 0,
      pagination: {
        el: `#${paginationId}`,
        clickable: true,
      },
    });
    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy(true, true);
        swiperRef.current = null;
      }
    };
  }, [slides, sliderId, paginationId, swiperReady]);

  const handleAvailabilitySearch = async ({
    startDate,
    endDate,
    guests: guestsCountFromForm,
  }) => {
    setBookingError("");
    setBookingHtml("");
    setBookingLoading(true);

    const toIsoDate = (value) => {
      if (!value) return "";
      const normalized = new Date(
        value.getTime() - value.getTimezoneOffset() * 60000,
      );
      return normalized.toISOString().slice(0, 10);
    };

    try {
      const baseParams = {
        listingId: data.id || listing?.id || "",
        checkIn: toIsoDate(startDate),
        checkOut: toIsoDate(endDate),
        guests: String(guestsCountFromForm || 1),
      };
      window.guestyBookingParams = {
        listingId: baseParams.listingId,
        checkIn: baseParams.checkIn,
        checkOut: baseParams.checkOut,
        guests: baseParams.guests,
      };
      // בדיקת זמינות לפני הצגת השורטקוד
      const availabilityUrl = new URL(
        "https://seasideeilat.co.il/wp-json/guesty/v1/search-available",
      );
      availabilityUrl.search = new URLSearchParams(baseParams).toString();
      const availabilityRes = await fetch(availabilityUrl.toString());
      const availabilityData = await availabilityRes.json();

      const available =
        availabilityData?.success &&
        Array.isArray(availabilityData.listings) &&
        availabilityData.listings.some(
          (l) => l._id === (data.id || listing?.id),
        );

      if (!available) {
        setBookingError("הווילה לא זמינה, אנא בחר תאריכים אחרים.");
        return true;
      }

      const ajaxParams = new URLSearchParams({
        action: "render_guesty_results",
        ...baseParams,
      });

      const res = await fetch(
        `/wp-admin/admin-ajax.php?${ajaxParams.toString()}`,
      );
      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const html = await res.text();
      setBookingHtml(html);
      if (window.initGuestyBooking) {
        window.initGuestyBooking();
      }
    } catch (err) {
      console.error("Failed to render booking results", err);
      setBookingError("שגיאה בטעינת התוצאות, נסה שוב.");
    } finally {
      setBookingLoading(false);
    }

    return true;
  };

  useEffect(() => {
    if (autoPrefilled) return;

    const params = new URLSearchParams(window.location.search);
    const checkInParam = params.get("checkIn") || params.get("checkin");
    const checkOutParam = params.get("checkOut") || params.get("checkout");
    const guestsParam = Number(params.get("guests")) || 1;

    const startDate = parseDate(checkInParam);
    const endDate = parseDate(checkOutParam);

    if (startDate || endDate || params.has("guests")) {
      setPrefillSearch({ start: startDate, end: endDate, guests: guestsParam });
    }

    if (startDate && endDate) {
      setAutoPrefillActive(true);
      setAutoPrefilled(true);
      handleAvailabilitySearch({
        startDate,
        endDate,
        guests: guestsParam,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrefilled]);
  useEffect(() => {
    console.log("🟢 useEffect bookingHtml fired", bookingHtml);

    if (!bookingHtml) return;

    requestAnimationFrame(() => {
      console.log("🟢 RAF after bookingHtml");
      if (window.initGuestyBooking) {
        console.log("🟢 calling initGuestyBooking");
        window.initGuestyBooking();
      } else {
        console.warn("❌ initGuestyBooking not found");
      }
    });
  }, [bookingHtml]);
  // if (!listing || !slides.length) {
  //   return (
  //     <div className="listing-detail-page" dir="rtl">
  //       <div className="loader-wrap">
  //         <div className="lds-dual-ring"></div>
  //       </div>
  //     </div>
  //   );
  // }

  const propertyTypeText = propertyTypeLabel(data.propertyType);
  const roomTypeText = roomTypeLabel(data.roomType);

  return (
    <div id="listing-detail-root" className="listing-detail-page" dir="rtl">
      <div className="ld-hero">
        <div className="main-image ld-slider">
          <div className="swiper ld-main-swiper" id={sliderId}>
            <div className="swiper-wrapper">
              {slides.map((src, idx) => (
                <div className="swiper-slide" key={idx}>
                  <img
                    src={src}
                    alt={`${data.title} ${idx + 1}`}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
            <div className="swiper-pagination" id={paginationId}></div>
          </div>
        </div>
        <div className="ld-thumbs">
          {thumbs.map((img, idx) => (
            <div className="ld-thumb" key={idx}>
              <img src={img} alt={`${data.title} תמונה ממוזערת ${idx + 1}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="ld-body">
        <aside className="ld-sidebar">
          {bookingHtml ? (
            <div
              id="booking-root"
              className="ld-booking"
              dangerouslySetInnerHTML={{ __html: bookingHtml }}
            />
          ) : autoPrefillActive && bookingLoading ? (
            <div className="ld-booking ld-booking--loading">
              <div className="lds-dual-ring"></div>
              <span>טוען את פרטי הדירה</span>
            </div>
          ) : (
            <AvailabilityCard
              listingId={data.id || listing?.id}
              onSearch={handleAvailabilitySearch}
              initialStart={prefillSearch.start}
              initialEnd={prefillSearch.end}
              initialGuests={prefillSearch.guests}
            />
          )}
          {bookingError && <div className="avail-error">{bookingError}</div>}
          {/* {bookingLoading && !bookingHtml && (
            <div className="avail-title" style={{ marginTop: 8 }}>
              טוען תוצאות...
            </div>
          )} */}

          <div className="ld-sidebar-card ld-important">
            <h4>מידע חשוב</h4>
            <ul className="ld-important-list">
              <li>
                <span className="ld-important-icon">
                  <i className="bi bi-check-lg"></i>
                </span>
                <span>צ׳ק-אין: החל מהשעה 15:00.</span>
              </li>
              <li>
                <span className="ld-important-icon">
                  <i className="bi bi-check-lg"></i>
                </span>
                <span>צ׳ק-אאוט: עד השעה 11:00.</span>
              </li>
              <li>
                <span className="ld-important-icon">
                  <i className="bi bi-check-lg"></i>
                </span>
                <span>הכנסת בעלי חיים אינה מותרת.</span>
              </li>
              <li>
                <span className="ld-important-icon">
                  <i className="bi bi-check-lg"></i>
                </span>
                <span>אירוח מגיל 24 ומעלה </span>
              </li>
            </ul>

            <h5 className="ld-subtitle">תנאי תשלום</h5>
            <ul className="ld-bullet-list">
              <li>
                <span className="ld-bullet" aria-hidden="true"></span>
                <span>בעת ביצוע הזמנה חיוב 50% מסכום ההזמנה.</span>
              </li>
              <li>
                <span className="ld-bullet" aria-hidden="true"></span>
                <span>יתרת התשלום תחויב יום לפני הגעה.</span>
              </li>
            </ul>

            <h5 className="ld-subtitle">מדיניות ביטולים</h5>
<ul className="ld-bullet-list">
  <li>
    <span className="ld-bullet" aria-hidden="true"></span>
    <span>
      ביטול עד 5 ימים מלאים לפני מועד ההגעה – חיוב בדמי ביטול בסך 5% ממחיר
      העסקה או 100 ₪ (הנמוך מביניהם).
    </span>
  </li>
  <li>
    <span className="ld-bullet" aria-hidden="true"></span>
    <span>ביטול בפחות מ-5 ימים לפני מועד ההגעה – חיוב מלא.</span>
  </li>
</ul>

<h5 className="ld-subtitle">שימו לב</h5>
<ul className="ld-bullet-list">
  <li>
    <span className="ld-bullet" aria-hidden="true"></span>
    <span>
      בקשות ביטול מתקבלות ומטופלות בשעות הפעילות בלבד: ראשון–חמישי
      09:00–17:00.
    </span>
  </li>
  <li>
    <span className="ld-bullet" aria-hidden="true"></span>
    <span>
      פנייה שנשלחה מחוץ לשעות הפעילות, כולל שישי ושבת/חגים, תיחשב כאילו
      התקבלה ביום העסקים הבא.
    </span>
  </li>
</ul>
</div>
</aside>

        <div className="ld-card">
          <div className="ld-path">
            {cityLabel(data.city)} • {titleLabel(data.title)}
          </div>
          <div className="ld-rating">
            <span className="stars">
              <i className="bi bi-star-fill"></i>
              <i className="bi bi-star-fill"></i>
              <i className="bi bi-star-fill"></i>
              <i className="bi bi-star-fill"></i>
              <i className="bi bi-star-fill"></i>
            </span>
            <span>
              {data.reviews?.avg || "4.8"} ({data.reviews?.total || 62} ביקורות)
            </span>
          </div>
          <h1 className="ld-title">{titleLabel(data.title)}</h1>

          {(!!data.summary || !!data.description) && (
            <div className="ld-section ">
              <h4>תיאור</h4>
              {!!data.summary && <p>{data.summary}</p>}
              {!!data.description && (
                <p style={{ marginTop: 6 }}>{data.description}</p>
              )}
            </div>
          )}

          {!!data.access && (
            <div className="ld-section">
              <h4>גישה לאורחים</h4>
              <p>{data.access}</p>
            </div>
          )}

          {!!data.neighborhood && (
            <div className="ld-section">
              <h4>שכונה</h4>
              <p>{neighborhoodLabel(data.neighborhood)}</p>
            </div>
          )}

          {!!data.directions && (
            <div className="ld-section">
              <h4>איך להגיע</h4>
              <p>{data.directions}</p>
            </div>
          )}

          {(!!data.checkin || !!data.checkout) && (
            <div className="ld-section">
              <h4>זמני צ׳ק אין וצ׳ק אאוט</h4>
              <p>
                {!!data.checkin && (
                  <>צ׳ק אין: {formatTimeHebrew(data.checkin)}</>
                )}
                {!!data.checkin && !!data.checkout && " • "}
                {!!data.checkout && (
                  <>צ׳ק אאוט: {formatTimeHebrew(data.checkout)}</>
                )}
              </p>
            </div>
          )}

          <div className="ld-section">
            <h4>מאפייני הנכס</h4>
            <div className="ld-feature-row">
              <div className="ld-feature">
                <span className="icon bi bi-house-door"></span>
                <span>{bedroomsLabel}</span>
              </div>
              <div className="ld-feature">
                <span className="icon bi bi-cloud-moon"></span>
                <span>{bedsLabel}</span>
              </div>
              <div className="ld-feature">
                <span className="icon bi bi-droplet-half"></span>
                <span>{bathroomsLabel}</span>
              </div>
              <div className="ld-feature">
                <span className="icon bi bi-people"></span>
                <span>{guestsLabel}</span>
              </div>
            </div>
            {(!!propertyTypeText || !!roomTypeText) && (
              <div style={{ marginTop: 10, color: "#555", fontSize: 14 }}>
                {!!propertyTypeText && <span>סוג נכס: {propertyTypeText}</span>}
                {!!propertyTypeText && !!roomTypeText && " • "}
                {!!roomTypeText && <span>סוג אירוח: {roomTypeText}</span>}
              </div>
            )}
          </div>

          <div className="ld-section">
            <h4>שירותים ואבזור</h4>
            <div className="ld-amenities">
              {data.amenities.slice(0, 12).map((a, idx) => {
                const label = amenityLabel(a);
                if (!label) return null;
                return (
                  <div className="ld-amenity" key={idx}>
                    <span className="icon bi bi-check2-circle"></span>
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ld-section">
            <h4>מיקום</h4>
            <div className="ld-map">
              <iframe
                title="מפה"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                  lng - delta
                }%2C${lat - delta}%2C${lng + delta}%2C${
                  lat + delta
                }&layer=mapnik&marker=${lat}%2C${lng}`}
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetail;
